import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import {
  Eye,
  ChevronLeft,
  Search,
  CheckCircle2,
  Circle,
  Sparkles,
  Award,
  TrendingUp,
  Loader2,
  Lock,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

export default function ViewAsStudentPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState("real"); // 'real' | 'fresh'
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [search, setSearch] = useState("");

  // Initial roster load
  useEffect(() => {
    setRosterLoading(true);
    axios
      .get(`${API}/educator/view-as-student/roster`, authHeaders())
      .then((r) => setRoster(r.data?.classes || []))
      .catch(() => setRoster([]))
      .finally(() => setRosterLoading(false));
  }, []);

  // Load snapshot when selection changes
  useEffect(() => {
    if (mode === "fresh") {
      setSnapshotLoading(true);
      axios
        .get(`${API}/educator/view-as-student-fresh`, authHeaders())
        .then((r) => setSnapshot(r.data))
        .catch(() => setSnapshot(null))
        .finally(() => setSnapshotLoading(false));
      return;
    }
    if (!selectedId) {
      setSnapshot(null);
      return;
    }
    setSnapshotLoading(true);
    axios
      .get(`${API}/educator/view-as-student/${selectedId}`, authHeaders())
      .then((r) => setSnapshot(r.data))
      .catch(() => setSnapshot(null))
      .finally(() => setSnapshotLoading(false));
  }, [mode, selectedId]);

  const filteredRoster = useMemo(() => {
    if (!search.trim()) return roster;
    const q = search.trim().toLowerCase();
    return roster
      .map((c) => ({
        ...c,
        students: c.students.filter(
          (s) =>
            (s.name || "").toLowerCase().includes(q) ||
            (s.email || "").toLowerCase().includes(q)
        ),
      }))
      .filter((c) => c.students.length > 0);
  }, [roster, search]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50/40 to-orange-50/40">
      <Navbar user={user} onLogout={onLogout} />
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900"
            data-testid="view-as-student-back-btn"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900 flex items-center gap-2">
            <Eye className="w-6 h-6 text-purple-600" /> View as Student
          </h1>
          <div className="bg-amber-100 border border-amber-300 text-amber-800 text-xs font-bold rounded-full px-3 py-1 inline-flex items-center gap-1">
            <Lock className="w-3 h-3" />
            READ-ONLY
          </div>
        </div>

        {/* Mode toggle */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-2 inline-flex gap-1 mb-5">
          <ModeButton
            active={mode === "real"}
            onClick={() => setMode("real")}
            label="Real student"
            sub="Pick from roster"
            testId="view-as-student-mode-real"
          />
          <ModeButton
            active={mode === "fresh"}
            onClick={() => setMode("fresh")}
            label="Fresh student"
            sub="Empty demo state"
            testId="view-as-student-mode-fresh"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5">
          {/* Roster */}
          {mode === "real" ? (
            <RosterPanel
              loading={rosterLoading}
              roster={filteredRoster}
              selectedId={selectedId}
              onSelect={setSelectedId}
              search={search}
              setSearch={setSearch}
            />
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 p-5 h-fit">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2">
                Demo mode
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                You're viewing the platform exactly as a brand-new student would on day 1.
                Use this for sales demos, parent walkthroughs, or principal previews.
              </p>
            </div>
          )}

          {/* Snapshot */}
          <div>
            {snapshotLoading ? (
              <SnapshotSkeleton />
            ) : !snapshot ? (
              <EmptyHint mode={mode} />
            ) : (
              <SnapshotView snapshot={snapshot} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ModeButton({ active, onClick, label, sub, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-left px-4 py-2 rounded-xl transition-all min-w-[160px] ${
        active
          ? "bg-gradient-to-br from-purple-500 to-fuchsia-500 text-white shadow-md"
          : "text-slate-700 hover:bg-slate-100"
      }`}
    >
      <div className="font-bold">{label}</div>
      <div className={`text-xs ${active ? "text-purple-100" : "text-slate-500"}`}>
        {sub}
      </div>
    </button>
  );
}

function RosterPanel({ loading, roster, selectedId, onSelect, search, setSearch }) {
  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 h-fit lg:sticky lg:top-4"
      data-testid="view-as-student-roster"
    >
      <div className="relative mb-3">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search students…"
          className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300"
          data-testid="view-as-student-search"
        />
      </div>
      {loading ? (
        <div className="flex justify-center py-6 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : roster.length === 0 ? (
        <p className="text-sm text-slate-500 italic px-2 py-4">
          No students enrolled yet. Share your class join code to get started.
        </p>
      ) : (
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {roster.map((cls) => (
            <div key={cls.class_id}>
              <div className="text-[10px] font-bold tracking-wider text-slate-500 uppercase px-1 mb-1">
                {cls.class_name}
              </div>
              <ul className="space-y-1">
                {cls.students.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => onSelect(s.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                        selectedId === s.id
                          ? "bg-purple-100 text-purple-900 ring-1 ring-purple-300"
                          : "hover:bg-slate-50 text-slate-700"
                      }`}
                      data-testid={`view-as-student-row-${s.id.slice(0, 6)}`}
                    >
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-400 to-fuchsia-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {(s.name || "S").charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate">{s.name}</div>
                        <div className="text-[11px] text-slate-500 truncate">{s.email}</div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyHint({ mode }) {
  return (
    <div
      className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center"
      data-testid="view-as-student-empty"
    >
      <div className="w-14 h-14 rounded-full bg-purple-100 text-purple-600 mx-auto mb-3 flex items-center justify-center">
        <Eye className="w-7 h-7" />
      </div>
      <h3 className="text-lg font-bold text-slate-900 mb-1">
        {mode === "fresh" ? "Loading demo student…" : "Pick a student to view"}
      </h3>
      <p className="text-sm text-slate-500 max-w-sm mx-auto">
        {mode === "fresh"
          ? "Hold tight — we're spinning up the empty demo state."
          : "Choose a student from your roster to see exactly what they see — dashboard, lessons, capstone, and AI plan. Nothing you do here saves changes."}
      </p>
    </div>
  );
}

function SnapshotSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 animate-pulse">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-14 h-14 rounded-full bg-slate-200" />
        <div className="space-y-2">
          <div className="h-4 w-40 bg-slate-200 rounded" />
          <div className="h-3 w-28 bg-slate-100 rounded" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[0, 1, 2].map((i) => (
          <div key={i} className="bg-slate-50 rounded-xl p-3 space-y-2">
            <div className="h-2 w-12 bg-slate-200 rounded" />
            <div className="h-6 w-14 bg-slate-200 rounded" />
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <div className="h-3 w-full bg-slate-100 rounded" />
        <div className="h-3 w-5/6 bg-slate-100 rounded" />
        <div className="h-3 w-4/6 bg-slate-100 rounded" />
      </div>
    </div>
  );
}

function SnapshotView({ snapshot }) {
  const s = snapshot.student || {};
  const p = snapshot.progress || {};
  const isFresh = snapshot.mode === "fresh";

  return (
    <div className="space-y-5" data-testid="view-as-student-snapshot">
      {/* Identity card */}
      <div className="bg-gradient-to-br from-purple-500 via-fuchsia-500 to-pink-500 rounded-2xl text-white p-5 sm:p-6 shadow-lg">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-2xl font-extrabold flex-shrink-0">
            {(s.name || "S").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-widest opacity-90 font-semibold">
              {isFresh ? "Demo · Brand-new student" : "Real student · Read-only"}
            </div>
            <h2 className="text-2xl font-extrabold mt-0.5">{s.name}</h2>
            {s.email && <div className="text-sm opacity-90 mt-0.5">{s.email}</div>}
            <div className="mt-2 inline-flex items-center gap-2 bg-white/20 rounded-full px-3 py-1 text-xs font-semibold">
              {s.learning_path === "middle_school" ? "🎒 Middle School" : "🎓 Standard"}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-5">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="font-bold">
              {p.lessons_completed} / {p.lessons_total} lessons completed
            </span>
            <span className="font-extrabold">{p.completion_pct}%</span>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all"
              style={{ width: `${Math.min(100, p.completion_pct || 0)}%` }}
            />
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard
          icon={Award}
          label="Points"
          value={s.points || 0}
          tone="amber"
        />
        <KpiCard
          icon={Sparkles}
          label="Badges"
          value={(s.badges || []).length}
          tone="purple"
        />
        <KpiCard
          icon={TrendingUp}
          label="Pitch Attempts"
          value={(snapshot.pitch_attempts || []).length}
          tone="emerald"
        />
        <KpiCard
          icon={CheckCircle2}
          label="Capstone Fields"
          value={`${Object.values(snapshot.capstone || {}).filter(Boolean).length}/7`}
          tone="rose"
        />
      </div>

      {/* Modules */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="font-bold text-slate-900 mb-3">Modules & lessons</h3>
        <ul className="space-y-3">
          {(snapshot.modules || []).map((m) => (
            <li
              key={m.id}
              className="border border-slate-100 rounded-xl p-3"
              data-testid={`view-as-student-module-${m.order}`}
            >
              <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                <span className="font-bold text-slate-900 text-sm">
                  {m.order}. {m.title}
                </span>
                <span
                  className={`text-xs font-bold rounded-full px-2 py-0.5 ${
                    m.completion_pct === 100
                      ? "bg-emerald-100 text-emerald-700"
                      : m.completion_pct > 0
                      ? "bg-amber-100 text-amber-700"
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {m.lessons_completed}/{m.lesson_count}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                {m.lessons.map((l) => (
                  <div
                    key={l.id}
                    className={`text-xs px-2 py-1.5 rounded-lg flex items-center gap-1.5 ${
                      l.completed
                        ? "bg-emerald-50 text-emerald-800"
                        : "bg-slate-50 text-slate-500"
                    }`}
                  >
                    {l.completed ? (
                      <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                    ) : (
                      <Circle className="w-3.5 h-3.5 flex-shrink-0" />
                    )}
                    <span className="truncate">{l.title}</span>
                  </div>
                ))}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Capstone */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="font-bold text-slate-900 mb-3">Mini Business Plan v1 (Capstone)</h3>
        {Object.values(snapshot.capstone || {}).filter(Boolean).length === 0 ? (
          <p className="text-sm text-slate-500 italic">
            No capstone fields filled in yet.
          </p>
        ) : (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              ["ms_business_idea", "Business Idea"],
              ["ms_customer_profile", "Customer Profile"],
              ["ms_brand", "Brand"],
              ["ms_money_plan", "Money Plan"],
              ["ms_marketing", "Marketing"],
              ["ms_operations", "Operations"],
              ["ms_launch", "Launch"],
            ].map(([k, label]) => {
              const val = (snapshot.capstone || {})[k];
              return (
                <div key={k} className="bg-slate-50 rounded-xl p-3">
                  <dt className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                    {label}
                  </dt>
                  <dd
                    className={`text-sm leading-snug ${
                      val ? "text-slate-800" : "text-slate-400 italic"
                    }`}
                  >
                    {val || "—"}
                  </dd>
                </div>
              );
            })}
          </dl>
        )}
      </div>

      {/* AI Plan */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="font-bold text-slate-900 mb-3">AI Business Plan</h3>
        {!snapshot.ai_plan ? (
          <p className="text-sm text-slate-500 italic">No AI plan generated yet.</p>
        ) : (
          <div>
            <div className="text-xs text-slate-500 mb-2">
              Version {snapshot.ai_plan.version} · Generated{" "}
              {snapshot.ai_plan.generated_at
                ? new Date(snapshot.ai_plan.generated_at).toLocaleDateString()
                : "—"}
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 max-h-72 overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-slate-800 font-mono">
              {typeof snapshot.ai_plan.plan === "string"
                ? snapshot.ai_plan.plan
                : JSON.stringify(snapshot.ai_plan.plan, null, 2)}
            </div>
          </div>
        )}
      </div>

      {/* Pitch Attempts */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="font-bold text-slate-900 mb-3">Pitch Practice History</h3>
        {(snapshot.pitch_attempts || []).length === 0 ? (
          <p className="text-sm text-slate-500 italic">No pitch attempts yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {snapshot.pitch_attempts.map((pa, i) => (
              <li
                key={pa.id}
                className="flex items-center justify-between bg-slate-50 rounded-lg p-2 text-sm"
              >
                <span className="font-mono text-xs text-slate-500">
                  #{i + 1} ·{" "}
                  {pa.created_at ? new Date(pa.created_at).toLocaleDateString() : ""}
                </span>
                <span className="font-bold text-slate-900">{pa.percent || pa.score}%</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, tone }) {
  const tones = {
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    purple: "bg-purple-50 border-purple-200 text-purple-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone] || tones.amber}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold opacity-80 mb-1">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className="text-2xl font-extrabold">{value}</div>
    </div>
  );
}
